import { solana } from '@/constants/chain';

const EVM_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;

/**
 * Normalizes an address for use as a lookup / Redis key when chainId is unknown.
 * EVM hex addresses are lowercased (case-insensitive). Non-EVM addresses
 * (e.g. Solana base58) are preserved as-is.
 *
 * Prefer {@link normalizeTokenAddress} when chainId is available.
 */
export const normalizeAddressKey = (address: string): string =>
  EVM_ADDRESS_PATTERN.test(address) ? address.toLowerCase() : address;

/**
 * Normalizes a token address for use as a lookup key.
 * EVM addresses are lowercased (hex is case-insensitive).
 * Solana addresses are preserved as-is (base58 is case-sensitive).
 */
export const normalizeTokenAddress = (address: string, chainId: number): string => {
  return chainId === solana.id ? address : address.toLowerCase();
};

/**
 * Builds a normalized "address:chainId" key for Map lookups and cache keys.
 */
export const tokenKey = (address: string, chainId: number): string =>
  `${normalizeTokenAddress(address, chainId)}:${chainId}`;

/**
 * Compares two token addresses when chainId is unknown. EVM hex addresses match
 * case-insensitively; non-EVM addresses (e.g. Solana base58) match exactly.
 */
export const isSameTokenAddress = (a: string | undefined, b: string | undefined): boolean =>
  !!a && !!b && normalizeAddressKey(a) === normalizeAddressKey(b);
